// B-Final-Fix (P7) — Public Verifier Landing Page.
// Consumes /api/compliance-proof/verify/{token} in redacted mode. No
// authentication. Renders an insurer-friendly verdict ("Chain Intact"
// or "Drift Detected") + verified_at + a short plain-language note.
// ERF brand palette. Responsive. Accessible.
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAVY = "#12233F";
const IVORY = "#F5F1E8";
const BRASS = "#C6A15B";
const GREEN = "#166534";
const RED = "#B91C1C";

function fmtIso(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function VerifyCompliance() {
  const { token } = useParams();
  const [state, setState] = useState("loading");   // loading | ok | drift | invalid
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      try {
        const r = await axios.get(`${API}/compliance-proof/verify/${token}`);
        if (cancelled) return;
        setPayload(r.data);
        setState(r.data?.drift ? "drift" : "ok");
      } catch (e) {
        if (cancelled) return;
        setState("invalid");
      }
    }
    run();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div
      data-testid="verify-compliance-page"
      style={{
        minHeight: "100vh",
        background: IVORY,
        fontFamily: "'Inter', system-ui, sans-serif",
        color: NAVY,
        display: "flex", flexDirection: "column",
      }}
    >
      <header
        style={{
          padding: "20px 24px",
          background: "#FFFFFF",
          borderBottom: `1px solid ${BRASS}55`,
          display: "flex", alignItems: "center", gap: 14,
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 40, height: 40, borderRadius: 4,
            background: NAVY, color: BRASS,
            display: "grid", placeItems: "center",
            fontFamily: "'Playfair Display', serif",
            fontSize: 20, fontWeight: 700,
          }}
        >E</div>
        <div>
          <div
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10, letterSpacing: "0.28em",
              textTransform: "uppercase", color: BRASS,
            }}
          >Compliance Proof · Verifier</div>
          <div
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 15, fontWeight: 700, color: NAVY,
            }}
          >ERF ENTERPRISES LLC</div>
        </div>
      </header>

      <main
        style={{
          flex: 1, maxWidth: 720, width: "100%",
          margin: "0 auto", padding: "56px 24px 64px",
        }}
      >
        {state === "loading" && (
          <div
            data-testid="verify-state-loading"
            style={{
              padding: 40, borderRadius: 6,
              background: "#FFFFFF", textAlign: "center",
              border: `1px solid ${BRASS}55`,
            }}
          >
            <Loader2 size={32} className="animate-spin" style={{ color: BRASS }} />
            <div
              style={{
                marginTop: 12, fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
                color: `${NAVY}99`,
              }}
            >Verifying signed proof…</div>
          </div>
        )}

        {state === "ok" && (
          <VerdictCard
            testid="verify-state-ok"
            icon={<ShieldCheck size={56} strokeWidth={1.4} color={GREEN} />}
            barColor={GREEN}
            eyebrow="Chain Intact"
            heading="Verified — Chain Intact"
            verifiedAt={payload?.verified_at}
          />
        )}

        {state === "drift" && (
          <VerdictCard
            testid="verify-state-drift"
            icon={<ShieldAlert size={56} strokeWidth={1.4} color={RED} />}
            barColor={RED}
            eyebrow="Drift Detected"
            heading="Drift Detected — Do Not Trust"
            verifiedAt={payload?.verified_at}
            drift
          />
        )}

        {state === "invalid" && (
          <VerdictCard
            testid="verify-state-invalid"
            icon={<ShieldAlert size={56} strokeWidth={1.4} color={RED} />}
            barColor={RED}
            eyebrow="Invalid or Expired"
            heading="This link is no longer valid"
            invalid
          />
        )}

        <p
          style={{
            marginTop: 24, fontSize: 12,
            color: `${NAVY}99`, lineHeight: 1.6,
          }}
        >
          The verifier confirms the record's audit chain against the state ERF
          ENTERPRISES LLC signed at generation. No customer, financial, or
          identifier data is disclosed on this page.
        </p>
      </main>
    </div>
  );
}

function VerdictCard({ testid, icon, barColor, eyebrow, heading, verifiedAt, drift, invalid }) {
  return (
    <div
      data-testid={testid}
      role="status"
      aria-live="polite"
      style={{
        background: "#FFFFFF",
        borderRadius: 6,
        padding: 40,
        borderTop: `4px solid ${barColor}`,
        boxShadow: "0 20px 40px -30px rgba(11,37,69,0.28)",
        display: "flex", flexDirection: "column", gap: 18,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
        {icon}
        <div>
          <div
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11, letterSpacing: "0.22em",
              textTransform: "uppercase", color: barColor,
            }}
          >{eyebrow}</div>
          <h1
            style={{
              margin: 0,
              fontFamily: "'Playfair Display', serif",
              fontSize: 28, fontWeight: 700,
              color: NAVY, lineHeight: 1.2,
            }}
            data-testid="verify-heading"
          >{heading}</h1>
        </div>
      </div>

      {verifiedAt && (
        <div
          data-testid="verify-timestamp"
          style={{
            fontSize: 13, color: `${NAVY}CC`,
            paddingTop: 8, borderTop: `1px solid ${BRASS}33`,
          }}
        >
          <span
            style={{
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
              letterSpacing: "0.22em", textTransform: "uppercase",
              color: `${NAVY}99`,
            }}
          >Verified at</span>
          <div style={{ marginTop: 4, fontSize: 14, fontWeight: 500 }}>
            {fmtIso(verifiedAt)}
          </div>
        </div>
      )}

      <p
        data-testid="verify-explanation"
        style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: NAVY }}
      >
        {invalid
          ? "This code cannot be verified. It may have expired, been mistyped, or been revoked. Ask the sender to re-generate a fresh Compliance Proof."
          : drift
          ? "The record has changed since this Compliance Proof was generated. Do not rely on this document — request an updated proof from ERF ENTERPRISES LLC."
          : "This code confirms whether the record has changed since the Compliance Proof was generated. If the chain is intact, the document reflects the same state that ERF ENTERPRISES LLC certified."}
      </p>
    </div>
  );
}
